import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { removeStoredUser } from '../../utils/auth/userStorage';
import { useHistory } from 'react-router-dom';
import * as actions from '../../store/actions';
import * as setActions from '../../store/actions';
import { login } from '../../utils/functions/authenticate';
import {COLORS, media, FONT_SIZE, FONT_WEIGHT} from '../../components/ui/theme';
import { ModalShell } from '../../components/ui/ModalShell';
import {
  ModalHeader,
  ModalHeaderTitle,
  ModalCloseBtn,
  ModalBody,
  ModalFooter,
  ModalIconBadge,
  INK,
} from '../../components/ui/ModalDA';

/**
 * SessionTimeoutModal — Modal affichée quand la session JWT expire.
 *
 * Design v2 validé par Jonathan via preview HTML 2026-05-31 :
 * https://dev-jonathan.earniverse.io/previews/session-timeout-modal-v2.html
 *
 * Famille visuelle unifiée avec EpicGoodbyeModal / NotAvailableYetModal /
 * ConnectWalletModal v2 : header violet, icône horloge violette, bouton vert.
 *
 * Comportement : croix permet de fermer (reset isTimeout), Reconnect = action principale
 * (vide localStorage user + mémorise pathname pour redirect après login + redirige vers /).
 *
 * Migration P1 (2026-06-06) : clone (10 styled + 4 tokens drift) → ModalShell + primitives ModalDA.
 * Bonus a11y gratuit : ESC + scroll-lock + focus-trap via ModalShell.
 *
 * @see tasks/active/TRACK-2026-06-06-da-middleware-p1.md
 */

/* Paragraphe principal (taille/poids dédiés à ce template) */
const Primary = styled.p`
  margin: 0 0 1.5rem;
  font-size: 1.0625rem;
  font-weight: ${FONT_WEIGHT.semibold};
  color: ${INK};
  line-height: 1.45;
`;

/* Bouton CTA vert "Reconnect" : min-width 12rem (≠ ButtonPrimary 11rem) */
const ReconnectButton = styled.button`
  min-width: 12rem;
  padding: 0.75rem 1.5rem;
  background: ${COLORS.cta};
  color: #001a07;
  border: none;
  border-radius: 0.625rem;
  font-family: inherit;
  font-weight: ${FONT_WEIGHT.heavy};
  font-size: ${FONT_SIZE.sm};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0 0.5rem 1.5rem rgba(31, 203, 92, 0.32);
  transition: filter 0.12s;
  &:hover { filter: brightness(1.05); }
  &:active { filter: brightness(0.95); }
  ${media.sm`min-width: 0; width: 100%;`}
`;

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke={COLORS.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

function SessionTimeoutModal({ open, setOpen }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const handleClose = () => {
    dispatch(actions.setIsTimeout(false));
    setOpen(false);
  };

  const handleReconnect = async () => {
    dispatch(actions.setIsTimeout(false));
    // Mémoriser la page courante pour y revenir après re-login (si pas déjà mémorisée
    // par SessionExpiredOrAccessDenied lors du redirect du Route guard).
    if (window.location.pathname !== '/') {
      try {
        localStorage.setItem('_redirectAfterLogin', window.location.pathname + window.location.search);
      } catch (e) {}
    }
    removeStoredUser();
    /* Fix 2026-06-06 : déclencher login() directement (popup MetaMask signature) au lieu
       de `window.location.href = '/'`. L'ancien comportement ne reloadait pas si on était
       déjà sur '/' (cas post-redirect du Route guard SessionExpiredOrAccessDenied) →
       Reconnect "ne faisait rien" visuellement. login() :
         1. Demande signature MetaMask via personal_sign
         2. POST /auth/signin → JWT
         3. setUser dans Redux + _user dans localStorage
         4. Lit _redirectAfterLogin → history.push vers la page admin d'origine
       Si pas de window.ethereum (no wallet) → login() ne fait rien, fallback reload home. */
    try {
      await login(dispatch, setActions.setLoading, setActions.setUser, history);
    } catch (e) {
      // Fallback : pas de MetaMask, ou login a planté → reload home pour clean state
      window.location.href = '/';
    }
  };

  return (
    <ModalShell isOpen={open} onClose={handleClose} $width="30rem" aria-label="Session expired">
      <ModalHeader>
        <ModalHeaderTitle>{t('SessionTimeout')}</ModalHeaderTitle>
        <ModalCloseBtn onClick={handleClose} aria-label={t('Close')}>✕</ModalCloseBtn>
      </ModalHeader>
      <ModalBody>
        <ModalIconBadge $bg={COLORS.accentSoft} $border $size="5rem" $mb="1.25rem">
          <ClockIcon />
        </ModalIconBadge>
        <Primary>{t('SessionExpired')}</Primary>
      </ModalBody>
      <ModalFooter>
        <ReconnectButton onClick={handleReconnect}>{t('ReconnectBtn')} ▸</ReconnectButton>
      </ModalFooter>
    </ModalShell>
  );
}

SessionTimeoutModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func,
};

SessionTimeoutModal.defaultProps = {
  setOpen: () => {},
};

export default SessionTimeoutModal;
